import {makeAutoObservable, runInAction, reaction} from 'mobx';
import { AvailabilityModel, AvailabilityResponse} from '../../model/professional/availability-model';
import availabilityStore from "../../store/availability-store";
import {HTTP_STATUS_CODE} from "../../utils/UHttp";
import toastStore from "../../store/toast-store";
import { formatDateInTimeZone } from '../../utils/timezone';
import i18next from 'i18next';
import companyStore from "../../store/company-store";

export interface EditableSlot {
  ids: string[];
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  originalStartTime: string;
  originalEndTime: string;
  isNew: boolean;
  isDeleted: boolean;
  hasBookings?: boolean;
}

export interface RecurringConfig {
  recurrenceKey: string;
  recurrenceStartDate: string;
  recurrenceEndDate: string;
  isFullYear: boolean;
  slots: EditableSlot[];
}

export class AvailabilityViewModel {
  // États pour gérer les créneaux


  addRecurringSlots: any[] = [];
  addOneTimeSlots: any[] = [];

  startDate: string = formatDateInTimeZone(new Date(), "yyyy-MM-dd")
  endDate: string = formatDateInTimeZone(new Date(), "yyyy-MM-dd")
  startDateOneTime: string = formatDateInTimeZone(new Date(), "yyyy-MM-dd")
  startTime: string = "09:30"
  endTime: string = "17:00"
  hasStartAndEnd: boolean = false
  isVisibleDrawer: boolean = false
  showCustomPeriod: boolean = false
  
  // États pour le modal de duplication des créneaux
  isDuplicateModalOpen: boolean = false;
  sourceSlot: any = null;


  isLoading: boolean = false;
  error: string | null = null;


  isConfirmDialogOpen: boolean = false;
  timeSlotToDeleteId: string= ""

  isEditModalOpen: boolean = false;
  selectedTimeSlot: any = null

  // Propriétés pour l'édition d'un créneau
  editDate: string = '';
  editStartTime: string = '';
  editEndTime: string = '';

  drawerOpen: boolean = false
  slotType: 'ponctuel' | 'recurrent' | null = null
  selectedCollaboratorId: string = '';

  // État mode édition en masse
  editDrawerOpen: boolean = false;
  isEditVisibleDrawer: boolean = false;
  isEditMode: boolean = false;
  editConfigs: RecurringConfig[] = [];
  selectedEditConfig: RecurringConfig | null = null;
  editConfigPeriodChanged: boolean = false;
  editConfigStartDate: string = '';
  editConfigEndDate: string = '';

  protected availabilityModel: AvailabilityModel

  constructor() {
    makeAutoObservable(this);
    this.availabilityModel = AvailabilityModel.getInstance()

    // Réagir aux changements de collaborateur sélectionné
    reaction(
      () => this.selectedCollaboratorId,
      (collaboratorId) => {
        if (collaboratorId) {
          this.handleGetTimeSlotsByProfessional().then();
        }
      }
    );
  }

  get getShowCustomPeriod() {
    return this.showCustomPeriod
  }


  get getIsVisibleDrawer() {
    return this.isVisibleDrawer
  }

  setError(error: string) {
    this.error = error
  }

  get getError(){
    return this.error
  }
  
  // Calcule l'heure de fin minimale (15 minutes après l'heure de début)
  calculateMinEndTime(startTime: string): string {
    // Si startTime est vide ou invalide, retourner une valeur par défaut
    if (!startTime || !startTime.includes(':')) return '17:00';
    
    // Vérifier que le format est correct
    const parts = startTime.split(':');
    if (parts.length !== 2) return '17:00';
    
    // Tenter de convertir en nombres et vérifier que ce sont des nombres valides
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    if (isNaN(hours) || isNaN(minutes)) return '17:00';
    
    // Calculer la nouvelle heure
    let totalMinutes = hours * 60 + minutes + 15; // Ajouter 15 minutes minimum
    
    // Convertir en heures:minutes
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;
    
    // Formater en format HH:MM
    return `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;
  }

  get getHasStartAndEnd() {
    return this.hasStartAndEnd
  }

  get getStartDate() {
    return this.startDate
  }

  get getStartDateOneTime() {
    return this.startDateOneTime
  }

  get getEndDate() {
    return this.endDate
  }

  get getStartTime() {
    return this.startTime
  }

  get getEndTime() {
    return this.endTime
  }

  // Méthode pour convertir une heure au format HH:MM en objet Date
  private parseTime(timeString: string): Date {
    const [hours, minutes] = timeString.split(':').map(Number)
    const today = formatDateInTimeZone(new Date(), 'yyyy-MM-dd')
    // Créer une date avec l'heure spécifiée en respectant le fuseau horaire
    const dateTimeString = `${today}T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`
    return new Date(dateTimeString)
  }

  get getAddOneTimeSlots() {
    return this.addOneTimeSlots
  }

  get getAvailability(){
    return availabilityStore.getAvailability
  }

  get getDrawerOpen (){
    return this.drawerOpen
  }

  get getEditDrawerOpen() {
    return this.editDrawerOpen
  }

  get getIsEditVisibleDrawer() {
    return this.isEditVisibleDrawer
  }

  setShowCustomPeriod(showCustomPeriod: boolean) {
    this.showCustomPeriod = showCustomPeriod
  }
  
  setIsVisibleDrawer(isVisible: boolean) {
    this.isVisibleDrawer = isVisible
  }

  setDrawerOpen(drawerOpen: boolean) {
    this.drawerOpen = drawerOpen
  }
  
  // Getters pour le modal de duplication
  get getIsDuplicateModalOpen(): boolean {
    return this.isDuplicateModalOpen;
  }
  
  get getSourceSlot(): any {
    return this.sourceSlot;
  }
  
  // Méthodes pour gérer le modal de duplication
  openDuplicateModal(slot: any) {
    runInAction(() => {
      this.sourceSlot = slot;
      this.isDuplicateModalOpen = true;
    });
  }
  
  closeDuplicateModal() {
    runInAction(() => {
      this.isDuplicateModalOpen = false;
      this.sourceSlot = null;
    });
  }

  openDrawer() {
    this.drawerOpen = true;
    // Un petit délai pour que la transition CSS fonctionne correctement
    setTimeout(() => this.setIsVisibleDrawer(true), 10);
  }

  closeDrawer() {
    this.setIsVisibleDrawer(false);
    // Attendre que l'animation se termine avant de fermer
    setTimeout(() => {
      this.setDrawerOpen(false);
      // Réinitialiser le type de créneau à null seulement à la fermeture
      this.slotType = null;
    }, 300);
  }

  toggleDrawer(){
    if (this.drawerOpen) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  get getSlotType() {
    return  this.slotType
  }

  setSlotType(slotType: 'ponctuel' | 'recurrent' | null){
    this.slotType = slotType
  }

  setSelectedCollaboratorId(id: string): void {
    this.selectedCollaboratorId = id;
  }

  setIsConfirmDialogOpen(timeSlotToDeleteId?: string) {
    this.isConfirmDialogOpen = !this.isConfirmDialogOpen
    if (timeSlotToDeleteId) {
      this.timeSlotToDeleteId = timeSlotToDeleteId
    }
  }

  setIsEditModalOpen(selectedTimeSlot?: any) {
    this.isEditModalOpen = !this.isEditModalOpen
    
    if(selectedTimeSlot) {
      this.selectedTimeSlot = selectedTimeSlot
      this.editDate = selectedTimeSlot.date
      this.editStartTime = selectedTimeSlot.start
      this.editEndTime = selectedTimeSlot.end

    } else if (!this.isEditModalOpen) {
      this.selectedTimeSlot = null
    }
  }

  setStartDate(startDate: string) {
    this.startDate = startDate
  }

  setStartDateOneTime(startDateOneTime: string) {
    this.startDateOneTime = startDateOneTime
  }

  setEndDate(endDate: string) {
    this.endDate = endDate
  }

  setStartTime(startTime: string) {
    this.startTime = startTime
  }

  setEndTime(endTime: string) {
    this.endTime = endTime
  }

  
  setEditStartTime(time: string) {
    this.editStartTime = time
  }
  
  setEditEndTime(time: string) {
    this.editEndTime = time

  }
  
  // Gérer la sauvegarde d'un créneau modifié
  async updateTimeSlot() {
    if (!this.selectedTimeSlot) return
    const selectedCompanyId = companyStore.getIdCompanySelectedDefault;
    const updatedTimeSlot = {
      companyId: selectedCompanyId,
      id: this.selectedTimeSlot.id,
      startTime: this.editStartTime,
      endTime: this.editEndTime
    }

    this.error = ""
    if (!this.isEndTimeAfterStartTime(updatedTimeSlot.startTime , updatedTimeSlot.endTime)) {
      this.error = i18next.t('availability.endTimeBeforeStartTime')
      return
    }

    try {
      this.isLoading = true;
      const response: AvailabilityResponse = await this.availabilityModel.updateTimeSlot(updatedTimeSlot)

      if(response.status === HTTP_STATUS_CODE.OK){
        // Mettre à jour le store avec le créneau modifié
        if (response.data && response.data.length > 0) {
          availabilityStore.updateAvailability(response.data[0])
        }
        toastStore.success(response.message);
      } else {
        toastStore.error(response.message);
      }
    } catch (error) {
      toastStore.error("Erreur lors de la mise à jour du créneau")
    } finally {
      runInAction(() => {
        this.setIsEditModalOpen()
        this.isLoading = false;
        this.error = null;
      });
    }

  }
  
  // Initialise les données à partir du modèle
  async handleGetTimeSlotsByProfessional() {
    try {
      this.isLoading = true;

      const response: AvailabilityResponse = await this.availabilityModel.getTimeSlotsByProfessional(
        undefined,
        undefined,
        this.selectedCollaboratorId
      )

      if(response.status === HTTP_STATUS_CODE.OK){
        availabilityStore.setAvailability(response.data)
      }

    } catch (error) {
      runInAction(() => {
        this.isLoading = false;
        this.error = 'Erreur lors du chargement des données';
        console.error('Erreur lors de l\'initialisation:', error);
      });
      console.error("error AvailabilityViewModel.handleGetTimeSlotsByProfessional", error)
      toastStore.error('Erreur lors du chargement des données');
    } finally {
      runInAction(() => {
        this.isLoading = false;
        this.error = null;
      });
    }
  }

  // Fonction pour ajouter un créneau récurrent
  addRecurringSlotInList =  (dayOfWeek: number): void => {
      runInAction(() => {
        this.addRecurringSlots = [...this.addRecurringSlots, {
            id: `recurring-${Date.now()}-${dayOfWeek}`,
          dayOfWeek,
          startTime: this.startTime,
          endTime: this.endTime,
          startDate: this.startDate,
          endDate: this.endDate,
        }];
        this.startTime = ""
        this.endTime = ""
      });

  };
  
  // Fonction pour dupliquer des créneaux d'un jour vers d'autres jours
  duplicateSlotsToDays = (sourceDayIndex: number, targetDayIndices: number[]): void => {
    runInAction(() => {
      // Récupérer tous les créneaux du jour source
      const sourceSlots = this.addRecurringSlots.filter(slot => slot.dayOfWeek === sourceDayIndex);
      
      if (sourceSlots.length === 0) return;
      
      // Pour chaque jour cible, créer des créneaux identiques à ceux du jour source
      const newSlots = targetDayIndices.flatMap(targetDayIndex => {
        return sourceSlots.map(sourceSlot => ({
          id: `recurring-${Date.now()}-${targetDayIndex}-${Math.random().toString(36).substring(2, 9)}`,
          dayOfWeek: targetDayIndex,
          startTime: sourceSlot.startTime,
          endTime: sourceSlot.endTime,
          startDate: sourceSlot.startDate,
          endDate: sourceSlot.endDate,
        }));
      });
      
      // Ajouter les nouveaux créneaux à la liste existante
      this.addRecurringSlots = [...this.addRecurringSlots, ...newSlots];
    });
  };
  
  // Méthode complète pour gérer la duplication avec sélection des jours
  handleDuplicateToSelectedDays = (targetDayIndices: number[]): void => {
    runInAction(() => {
      if (this.sourceSlot) {
        this.duplicateSlotsToDays(this.sourceSlot.dayOfWeek, targetDayIndices);
        this.closeDuplicateModal();
      }
    });
  };
  
  // Fonction pour supprimer un créneau récurrent
  removeRecurringSlotOfList = (id: string) => {
    runInAction(() => {
      this.addRecurringSlots = this.addRecurringSlots.filter(slot => slot.id !== id);
    });
  };

  addOneTimeSlotInList = (): void => {
    runInAction(() => {
      this.addOneTimeSlots = [...this.addOneTimeSlots, {
        id: `onetime-${Date.now()}`,
        date: this.startDateOneTime,
        startTime: this.startTime,
        endTime: this.endTime
      }];
      this.startTime = ""
      this.endTime = ""
    });
  };

  removeOneTimeSlotOfList = (id: number) => {
    runInAction(() => {
      this.addOneTimeSlots = this.addOneTimeSlots.filter((slot, i) => i !== id);
    });
  };

  handleAddOneTimeSlot = async () => {
    try {
      const selectedCompanyId = companyStore.getIdCompanySelectedDefault;
      const timeSlots = [];

      for (const slot of this.addOneTimeSlots) {
        timeSlots.push({
          startDate: slot.date,
          endDate: slot.date,
          startTime: slot.startTime,
          endTime: slot.endTime,
          type: "oneOff",
          status: "available"
        });
      }
      const data = {
        companyId: selectedCompanyId,
        collaboratorCompagnieId: this.selectedCollaboratorId,
        data: timeSlots
      }

      this.isLoading = true;

      const response: AvailabilityResponse = await this.availabilityModel.createOneTimeSlot(data)


      if(response.status === HTTP_STATUS_CODE.CREATED){
        availabilityStore.addNewAvailability(response.data)
        toastStore.success(response.message);
        this.cleanUp()
      } else {
        toastStore.error(response.message);
      }

    } catch (error: any) {
      toastStore.error('Une erreur est survenue lors de la création des créneaux ponctuel');
    } finally {
      runInAction(() => {
        this.toggleDrawer()
        this.isLoading = false;
        this.error = null;
      });
    }
  }

  // Fonction pour valider les créneaux récurrents
  handleAddRecurringSlots = async (): Promise<any> => {
    try {
      const selectedCompanyId = companyStore.getIdCompanySelectedDefault;
      if (this.addRecurringSlots.length === 0) {
        toastStore.error('Aucun créneau récurrent à ajouter');
        return false;
      }

      const currentYear = new Date().getFullYear();
      const today = new Date().toISOString().split('T')[0];


      // Créer l'objet de base
      const recurringTimeSlots = {
        companyId: selectedCompanyId,
        collaboratorCompagnieId: this.selectedCollaboratorId,
        startDate: this.showCustomPeriod ? this.startDate : today,
        endDate: this.showCustomPeriod ? this.endDate : `${currentYear}-12-31`,
        type: "recurring",
        status: "available",
        schedule: [] as any[]
      };

      // Créer un dictionnaire pour regrouper par jour
      const daySchedules: Record<number, { startTime: string, endTime: string }[]> = {};

      // Parcourir tous les créneaux et les regrouper par jour
      for (const slot of this.addRecurringSlots) {
        const day = slot.dayOfWeek;

        // Si ce jour n'existe pas encore dans le dictionnaire, l'initialiser
        if (!daySchedules[day]) {
          daySchedules[day] = [];
        }

        // Ajouter la plage horaire pour ce jour
        daySchedules[day].push({
          startTime: slot.startTime,
          endTime: slot.endTime
        });
      }

      // Convertir le dictionnaire en tableau pour le format final
      for (const day in daySchedules) {
        recurringTimeSlots.schedule.push({
          day: parseInt(day),
          timeRanges: daySchedules[day]
        });
      }

      this.isLoading = true;

      const response = await this.availabilityModel.createRecurringSlot(recurringTimeSlots);

      if(response.status === HTTP_STATUS_CODE.CREATED){
        availabilityStore.addNewAvailability(response.data)
        toastStore.success(response.message);
        this.cleanUp()
      } else {
        toastStore.error(response.message);
      }

      return true;
    } catch (error) {
      console.error('Erreur lors de la création des créneaux récurrents:', error);
      toastStore.error('Une erreur est survenue lors de la création des créneaux récurrents');
      return false;
    } finally {
      runInAction(() => {
        //this.toggleDrawer()
        this.isLoading = false;
        this.error = null;
      });
    }
  };


  handleDeleteTimeSlot = async () => {
    try {
      const response = await this.availabilityModel.deleteRecurringSlot(this.timeSlotToDeleteId);
      if(response.status === HTTP_STATUS_CODE.OK){
        availabilityStore.removeAvailability(this.timeSlotToDeleteId)
        toastStore.success(response.message);
      } else {
        toastStore.error(response.message);
      }

    } catch (error) {
      console.error('Erreur lors de la suppression du créneau horaire:', error);
      toastStore.error('Une erreur est survenue lors de la suppression du créneau horaire');
      return false;
    } finally {

      runInAction(() => {
        this.isConfirmDialogOpen = false
        this.isLoading = false;
        this.error = null;
      });
    }
  }

  cleanUp = () => {
    this.startDate = formatDateInTimeZone(new Date(), 'yyyy-MM-dd')
    this.endDate = formatDateInTimeZone(new Date(), 'yyyy-MM-dd')
    this.startTime = ""
    this.endTime = ""
    this.addOneTimeSlots = [];
    this.addRecurringSlots = [];
  }


  formatDate = (dateString: string): string => {
    return formatDateInTimeZone(new Date(dateString), 'EEEE d MMMM yyyy');
  };

  // Fonction pour obtenir le nom du jour de la semaine
  getDayName = (dayIndex: number) => {
    const date = new Date();
    date.setDate(date.getDate() - date.getDay() + dayIndex);
    return formatDateInTimeZone(date, 'EEEE');
  };

  // ===== Mode édition en masse =====

  openEditDrawer = () => {
    runInAction(() => {
      this.isEditMode = true;
      this.selectedEditConfig = null;
      this.editConfigs = this.getGroupedConfigs();
      this.editDrawerOpen = true;
      setTimeout(() => {
        runInAction(() => {
          this.isEditVisibleDrawer = true;
        });
      }, 10);
    });
  }

  closeEditDrawer = () => {
    runInAction(() => {
      this.isEditVisibleDrawer = false;
    });
    setTimeout(() => {
      runInAction(() => {
        this.isEditMode = false;
        this.selectedEditConfig = null;
        this.editConfigs = [];
        this.editConfigPeriodChanged = false;
        this.editConfigStartDate = '';
        this.editConfigEndDate = '';
        this.editDrawerOpen = false;
      });
    }, 300);
  }

  selectEditConfig = (config: RecurringConfig) => {
    runInAction(() => {
      this.selectedEditConfig = config;
      this.editConfigStartDate = config.recurrenceStartDate;
      this.editConfigEndDate = config.recurrenceEndDate;
      this.editConfigPeriodChanged = false;
    });
  }

  backToConfigList = () => {
    runInAction(() => {
      this.selectedEditConfig = null;
      this.editConfigPeriodChanged = false;
      this.editConfigs = this.getGroupedConfigs();
    });
  }

  getGroupedConfigs = (): RecurringConfig[] => {
    const availability = availabilityStore.getAvailability;
    const recurringSlots = availability.filter(s => s.type === 'recurring');

    if (recurringSlots.length === 0) return [];

    // Regrouper par clé de récurrence (startDate + endDate de la recurrence)
    const configMap = new Map<string, {
      recurrenceStartDate: string;
      recurrenceEndDate: string;
      slotsMap: Map<string, { ids: string[]; dayOfWeek: number; startTime: string; endTime: string; hasBookings: boolean }>;
    }>();

    for (const slot of recurringSlots) {
      const recurrence = slot.recurrence;
      let recStartDate = '';
      let recEndDate = '';

      if (recurrence && recurrence.startDate && recurrence.endDate) {
        recStartDate = new Date(recurrence.startDate as any).toISOString().split('T')[0];
        recEndDate = new Date(recurrence.endDate as any).toISOString().split('T')[0];
      } else {
        recStartDate = slot.startDate.split('T')[0];
        recEndDate = slot.endDate.split('T')[0];
      }

      const recurrenceKey = `${recStartDate}_${recEndDate}`;

      if (!configMap.has(recurrenceKey)) {
        configMap.set(recurrenceKey, {
          recurrenceStartDate: recStartDate,
          recurrenceEndDate: recEndDate,
          slotsMap: new Map()
        });
      }

      const config = configMap.get(recurrenceKey)!;
      const slotDate = new Date(slot.startDate);
      const dayOfWeek = slotDate.getDay();

      const slotKey = `${dayOfWeek}_${slot.startTime}_${slot.endTime}`;
      const hasBookings = slot.hasBookings || false;

      if (!config.slotsMap.has(slotKey)) {
        config.slotsMap.set(slotKey, {
          ids: [],
          dayOfWeek,
          startTime: slot.startTime,
          endTime: slot.endTime,
          hasBookings: false
        });
      }

      const slotData = config.slotsMap.get(slotKey)!;
      slotData.ids.push(slot.id);
      // Si au moins un créneau a des bookings, marquer le groupe comme ayant des bookings
      if (hasBookings) {
        slotData.hasBookings = true;
      }
    }

    const configs: RecurringConfig[] = [];
    Array.from(configMap.entries()).forEach(([key, value]) => {
      const currentYear = new Date().getFullYear();
      const isFullYear = value.recurrenceEndDate.endsWith(`${currentYear}-12-31`) ||
        value.recurrenceEndDate === `${currentYear}-12-31`;

      const slots: EditableSlot[] = [];
      Array.from(value.slotsMap.values()).forEach((slotData: any) => {
        slots.push({
          ids: slotData.ids,
          dayOfWeek: slotData.dayOfWeek,
          startTime: slotData.startTime,
          endTime: slotData.endTime,
          originalStartTime: slotData.startTime,
          originalEndTime: slotData.endTime,
          isNew: false,
          isDeleted: false,
          hasBookings: slotData.hasBookings || false
        });
      });

      slots.sort((a, b) => {
        const aDay = a.dayOfWeek === 0 ? 7 : a.dayOfWeek;
        const bDay = b.dayOfWeek === 0 ? 7 : b.dayOfWeek;
        return aDay - bDay;
      });

      configs.push({
        recurrenceKey: key,
        recurrenceStartDate: value.recurrenceStartDate,
        recurrenceEndDate: value.recurrenceEndDate,
        isFullYear,
        slots
      });
    });

    return configs;
  }

  markEditSlotDeleted = (dayOfWeek: number, slotIndex: number) => {
    runInAction(() => {
      if (!this.selectedEditConfig) return;
      const slotsForDay = this.selectedEditConfig.slots.filter(s => s.dayOfWeek === dayOfWeek && !s.isDeleted);
      if (slotIndex < slotsForDay.length) {
        slotsForDay[slotIndex].isDeleted = true;
      }
    });
  }

  updateEditSlotTime = (dayOfWeek: number, slotIndex: number, field: 'startTime' | 'endTime', value: string) => {
    runInAction(() => {
      if (!this.selectedEditConfig) return;
      const slotsForDay = this.selectedEditConfig.slots.filter(s => s.dayOfWeek === dayOfWeek && !s.isDeleted);
      if (slotIndex < slotsForDay.length) {
        slotsForDay[slotIndex][field] = value;
      }
    });
  }

  addEditSlotToDay = (dayOfWeek: number) => {
    runInAction(() => {
      if (!this.selectedEditConfig) return;
      const existingSlots = this.selectedEditConfig.slots.filter(s => s.dayOfWeek === dayOfWeek && !s.isDeleted);
      let startTime = '09:00';
      let endTime = '17:00';

      if (existingSlots.length > 0) {
        const lastSlot = existingSlots[existingSlots.length - 1];
        startTime = lastSlot.endTime;
        const [h, m] = startTime.split(':').map(Number);
        const endH = (h + 1) % 24;
        endTime = `${endH.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      }

      this.selectedEditConfig.slots.push({
        ids: [],
        dayOfWeek,
        startTime,
        endTime,
        originalStartTime: '',
        originalEndTime: '',
        isNew: true,
        isDeleted: false
      });
    });
  }

  updateEditConfigPeriod = (startDate: string, endDate: string) => {
    runInAction(() => {
      this.editConfigStartDate = startDate;
      this.editConfigEndDate = endDate;
      this.editConfigPeriodChanged = true;
    });
  }

  handleSaveConfig = async () => {
    if (!this.selectedEditConfig) return;

    const selectedCompanyId = companyStore.getIdCompanySelectedDefault!;

    try {
      this.isLoading = true;
      this.error = null;

      const deletedSlots = this.selectedEditConfig.slots.filter(s => s.isDeleted && !s.isNew);
      const modifiedSlots = this.selectedEditConfig.slots.filter(
        s => !s.isDeleted && !s.isNew &&
          (s.startTime !== s.originalStartTime || s.endTime !== s.originalEndTime)
      );
      const newSlots = this.selectedEditConfig.slots.filter(s => s.isNew && !s.isDeleted);

      // Si la période a changé, supprimer tout et recréer
      if (this.editConfigPeriodChanged) {
        const allIds = this.selectedEditConfig.slots
          .filter(s => !s.isNew)
          .flatMap(s => s.ids);

        if (allIds.length > 0) {
          const deleteResponse = await this.availabilityModel.bulkDeleteTimeSlots(selectedCompanyId, allIds);
          if (deleteResponse.status === HTTP_STATUS_CODE.OK) {
            availabilityStore.removeMultipleAvailabilities(deleteResponse.data?.deleted || allIds);
          }
        }

        // Recréer avec la nouvelle période et les slots actuels (non supprimés)
        const activeSlots = this.selectedEditConfig.slots.filter(s => !s.isDeleted);
        if (activeSlots.length > 0) {
          await this.createRecurringSlotsFromEdit(activeSlots, this.editConfigStartDate, this.editConfigEndDate);
        }
      } else {
        if (deletedSlots.length > 0) {
          const idsToDelete = deletedSlots.flatMap(s => s.ids);
          if (idsToDelete.length > 0) {
            const deleteResponse = await this.availabilityModel.bulkDeleteTimeSlots(selectedCompanyId, idsToDelete);
            if (deleteResponse.status === HTTP_STATUS_CODE.OK) {
              availabilityStore.removeMultipleAvailabilities(deleteResponse.data?.deleted || idsToDelete);
            } else {
              toastStore.error(deleteResponse.message);
            }
          }
        }

        // Mise à jour en masse
        if (modifiedSlots.length > 0) {
          const updates = modifiedSlots.flatMap(s =>
            s.ids.map(id => ({ id, startTime: s.startTime, endTime: s.endTime }))
          );
          if (updates.length > 0) {
            const updateResponse = await this.availabilityModel.bulkUpdateTimeSlots(selectedCompanyId, updates);
            if (updateResponse.status === HTTP_STATUS_CODE.OK && updateResponse.data?.updated) {
              availabilityStore.updateMultipleAvailabilities(updateResponse.data.updated);
            } else {
              toastStore.error(updateResponse.message);
            }
          }
        }

        // Création des nouveaux créneaux
        if (newSlots.length > 0) {
          await this.createRecurringSlotsFromEdit(
            newSlots,
            this.selectedEditConfig.recurrenceStartDate,
            this.selectedEditConfig.recurrenceEndDate
          );
        }
      }

      toastStore.success(i18next.t('availability.editSaveSuccess', 'Modifications enregistrées avec succès'));
      // Recharger les données
      await this.handleGetTimeSlotsByProfessional();
      this.closeEditDrawer();

    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toastStore.error(i18next.t('availability.editSaveError', 'Erreur lors de la sauvegarde'));
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  handleDeleteConfig = async () => {
    if (!this.selectedEditConfig) return;

    const selectedCompanyId = companyStore.getIdCompanySelectedDefault!;
    const allIds = this.selectedEditConfig.slots
      .filter(s => !s.isNew)
      .flatMap(s => s.ids);

    if (allIds.length === 0) return;

    try {
      this.isLoading = true;
      const response = await this.availabilityModel.bulkDeleteTimeSlots(selectedCompanyId, allIds);

      if (response.status === HTTP_STATUS_CODE.OK) {
        availabilityStore.removeMultipleAvailabilities(response.data?.deleted || allIds);
        toastStore.success(response.message);
        await this.handleGetTimeSlotsByProfessional();
        this.closeEditDrawer();
      } else {
        toastStore.error(response.message);
      }
    } catch (error) {
      console.error('Erreur lors de la suppression de la configuration:', error);
      toastStore.error(i18next.t('availability.deleteConfigError', 'Erreur lors de la suppression'));
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  private createRecurringSlotsFromEdit = async (slots: EditableSlot[], startDate: string, endDate: string) => {
    const selectedCompanyId = companyStore.getIdCompanySelectedDefault!;

    const daySchedules: Record<number, { startTime: string; endTime: string }[]> = {};
    for (const slot of slots) {
      if (!daySchedules[slot.dayOfWeek]) {
        daySchedules[slot.dayOfWeek] = [];
      }
      daySchedules[slot.dayOfWeek].push({ startTime: slot.startTime, endTime: slot.endTime });
    }

    const schedule = Object.entries(daySchedules).map(([day, timeRanges]) => ({
      day: parseInt(day),
      timeRanges
    }));

    const recurringData = {
      companyId: selectedCompanyId,
      collaboratorCompagnieId: this.selectedCollaboratorId,
      startDate,
      endDate,
      type: 'recurring',
      status: 'available',
      schedule
    };

    const response = await this.availabilityModel.createRecurringSlot(recurringData);
    if (response.status === HTTP_STATUS_CODE.CREATED) {
      availabilityStore.addNewAvailability(response.data);
    } else {
      toastStore.error(response.message);
    }
  }

  // ===== Fin mode édition en masse =====

  /**
   * Récupère les créneaux disponibles d'un prestataire pour une date donnée
   */
  async getProfessionalAvailability(collaboratorId: string, serviceId: string, serviceDuration: number, date: string, bufferTime?: number, selectedOptionIds?: string[]) {
    try {
      this.isLoading = true;
      const response = await this.availabilityModel.getProfessionalAvailability(
          collaboratorId,
        serviceId,
        serviceDuration,
        date,
        bufferTime,
        selectedOptionIds
      );

      if (response.status === HTTP_STATUS_CODE.OK) {
        return {
          success: true,
          data: response.data,
          message: response.message
        };
      } else {
        return {
          success: false,
          data: [],
          message: response.message
        };
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des disponibilités:', error);
      return {
        success: false,
        data: [],
        message: 'Une erreur est survenue lors de la récupération des disponibilités'
      };
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /**
   * Parse une chaîne d'heure (format "HH:MM") en objet { hour, minute }
   */
  private parseTimeString(timeString: string): { hour: number, minute: number } {
    const [hours, minutes] = timeString.split(':').map(Number);
    return { hour: hours, minute: minutes };
  }

  /**
   * Vérifie si l'heure de fin est après l'heure de début
   */
  private isEndTimeAfterStartTime(startTime: string, endTime: string): boolean {
    const start = this.parseTimeString(startTime);
    const end = this.parseTimeString(endTime);

    const startMinutes = start.hour * 60 + start.minute;
    const endMinutes = end.hour * 60 + end.minute;

    return endMinutes > startMinutes;
  }
}


// Créer une instance singleton du ViewModel
const availabilityViewModel = new AvailabilityViewModel();

// Hook personnalisé pour utiliser le ViewModel
export const useAvailabilityViewModel = () => {
  return availabilityViewModel;
};

// Exporter directement l'instance pour une utilisation dans des contextes non-React
export default availabilityViewModel;
